import { useState, useEffect } from "react";

// Components read the token via their own useState initializer, so a plain
// localStorage.clear() elsewhere (e.g. a logout button) never reaches
// long-lived instances of this hook (like the top-level AppContent), leaving
// them with a stale "logged in" user and causing redirect loops. This event
// lets every mounted useToken() instance resync the moment logout happens.
export const AUTH_CHANGE_EVENT = "scaniny:auth-changed";

// Clears auth state and notifies every useToken() instance to resync,
// instead of relying on each call site to remember to do both.
export const performLogout = ({ preserveKeys = [] } = {}) => {
  const preserved = {};
  preserveKeys.forEach((key) => {
    const value = localStorage.getItem(key);
    if (value !== null) preserved[key] = value;
  });

  localStorage.clear();

  Object.entries(preserved).forEach(([key, value]) => {
    localStorage.setItem(key, value);
  });

  window.dispatchEvent(new Event(AUTH_CHANGE_EVENT));
};

export const useToken = () => {
  const [token, setTokenInternal] = useState(() => {
    const storedToken = localStorage.getItem("token");
    // Validate token format before storing
    if (
      storedToken &&
      typeof storedToken === "string" &&
      storedToken.split(".").length === 3
    ) {
      return storedToken;
    }
    return null;
  });

  const [activeJournal, setActiveJournalInternal] = useState(() => {
    const storedJournal = localStorage.getItem("activeJournal");
    try {
      return storedJournal ? JSON.parse(storedJournal) : null;
    } catch (error) {
      console.error("Error parsing activeJournal from localStorage:", error);
      return null;
    }
  });

  const setToken = (newToken) => {
    // Validate token before storing
    if (
      newToken &&
      typeof newToken === "string" &&
      newToken.split(".").length === 3
    ) {
      localStorage.setItem("token", newToken);
      localStorage.setItem("locked", false);
      setTokenInternal(newToken);
    } else {
      console.error("Invalid token format:", newToken);
      // Clear invalid token
      localStorage.removeItem("token");
      setTokenInternal(null);
    }
  };

  const setActiveJournal = (journal) => {
    try {
      if (journal) {
        const journalString = JSON.stringify(journal);
        localStorage.setItem("activeJournal", journalString);
        setActiveJournalInternal(journal);
      } else {
        localStorage.removeItem("activeJournal");
        setActiveJournalInternal(null);
      }
    } catch (error) {
      console.error("Error setting active journal:", error);
    }
  };

  const clearActiveJournal = () => {
    localStorage.removeItem("activeJournal");
    setActiveJournalInternal(null);
  };

  // Clear invalid tokens on component mount
  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    if (storedToken && (!token || token !== storedToken)) {
      // Token exists in localStorage but not in state, validate it
      if (storedToken.split(".").length !== 3) {
        console.warn("Clearing invalid token from localStorage");
        localStorage.removeItem("token");
      }
    }
  }, [token]);

  // Resync state whenever performLogout() runs anywhere in the app, so
  // long-lived hook instances don't keep acting on a stale logged-in user.
  useEffect(() => {
    const resync = () => {
      const storedToken = localStorage.getItem("token");
      const validToken =
        storedToken && storedToken.split(".").length === 3
          ? storedToken
          : null;
      setTokenInternal(validToken);

      const storedJournal = localStorage.getItem("activeJournal");
      try {
        setActiveJournalInternal(
          storedJournal ? JSON.parse(storedJournal) : null,
        );
      } catch (error) {
        setActiveJournalInternal(null);
      }
    };

    window.addEventListener(AUTH_CHANGE_EVENT, resync);
    return () => window.removeEventListener(AUTH_CHANGE_EVENT, resync);
  }, []);

  return [token, setToken, activeJournal, setActiveJournal, clearActiveJournal];
};
